/**
 * Domínio: UI de Conexões (canais).
 *
 * Slots da página de Conexões. Acesse via `connections.slots.STATUS`.
 *
 * Slots num registro só (`connectionsSlots`); `slots`/`slotContext` e os tipos
 * `ConnectionSlotContext` derivam dele.
 */
import { defineSlotRegistry, legacySlotContextOf, slotNamesOf, t, type SlotContextMapOf } from "@/ui/slotSchema";

/**
 * Objeto de conexão (model Whatsapp) que o call-site passa em `context.connection`.
 * Campos conhecidos e úteis num slot — o dev tem autocomplete em vez de `any`.
 * `channelData` é genérico em `D`: o plugin passa a forma dos seus campos custom.
 */
export interface IWhatsappConnection<D = Record<string, any>> {
  id: number;
  name: string;
  /** CONNECTED | DISCONNECTED | OPENING | qrcode | PAIRING | TIMEOUT */
  status: string;
  /** ex: "whatsapp", "whatsapp_wwebjs", "whatsapp_hineken", "telegram". */
  channel: string;
  number?: string;
  qrcode?: string;
  isDefault?: boolean;
  allowGroup?: boolean;
  token?: string;
  provider?: string;
  companyId?: number;
  greetingMessage?: string;
  farewellMessage?: string;
  complationMessage?: string;
  outOfHoursMessage?: string;
  /** Campos custom do canal (JSONB) — tipados por `D` quando o plugin declara. */
  channelData?: D | null;
  updatedAt?: string;
  createdAt?: string;
}

/** Célula do card de conexão (status e ações recebem o mesmo). */
const connectionCard = {
  whatsappId: t.number,
  connectionId: t.number,
  channel: t.note(t.string, 'ex: "whatsapp", "whatsapp_wwebjs" — filtre por aqui!'),
  status: t.note(t.string, "CONNECTED | DISCONNECTED | OPENING | qrcode"),
  name: t.optional(t.string),
  number: t.optional(t.string),
  isDefault: t.optional(t.boolean),
  channelData: t.optional(t.note(t.typed<Record<string, any> | null>("Record<string, any> | null"), "campos custom do canal (JSONB)")),
  updatedAt: t.optional(t.string),
  connection: t.optional(t.note(t.typed<IWhatsappConnection>("IWhatsappConnection"), "o objeto Whatsapp COMPLETO (fallback)")),
} as const;

export const connectionsSlots = defineSlotRegistry({
  STATUS: {
    name: "connection-status",
    description:
      "Célula de Status do card de conexão. Envolva (wrap) para mostrar progresso de inicialização/sync no lugar " +
      'do indicador padrão ("Carregando 80%", "Sincronizando…").',
    context: connectionCard,
  },
  ACTIONS: {
    name: "connection-actions",
    description:
      "Célula de AÇÕES do card (Conectar/QR/Reconectar/Desconectar). Envolva para esconder os botões nativos " +
      '(slot.hide) e pôr os seus — ex: canal sem QR esconde "QR Code" e mostra "Importar sessão".',
    context: connectionCard,
  },
  FORM_PREVIEW: {
    name: "connection-form-preview",
    description:
      "Área de PREVIEW na aba custom do plugin no formulário de conexão (WhatsAppModal). `channelData` chega " +
      "atualizado a cada tecla (onContextChange), antes de salvar — prévia ao vivo do que está sendo configurado.",
    context: {
      whatsappId: t.optional(t.note(t.number, "ausente ao criar uma conexão nova (ainda sem id)")),
      channel: t.string,
      channelData: t.note(t.typed<Record<string, any>>("Record<string, any>"), "valores ATUAIS digitados dos campos custom"),
    },
  },
});

export const connections = {
  events: {},
  /** Slots de render — derivado de `connectionsSlots`. */
  slots: slotNamesOf(connectionsSlots),
  hooks: {},
  /** @deprecated Formato antigo, derivado do registro. Use `connectionsSlots`. */
  slotContext: legacySlotContextOf(connectionsSlots),
  registry: connectionsSlots,
} as const;

export type ConnectionsSlotContextMap = SlotContextMapOf<typeof connectionsSlots>;

/**
 * connection-status / connection-actions — célula da lista de Conexões.
 * Genérico em `D` = shape do `channelData` do canal (default: aberto).
 *
 *   interface HinekenData { token?: string; phoneNumber?: string }
 *   (slot: SlotApi<ConnectionSlotContext<HinekenData>>) => {
 *     slot.context.channelData?.phoneNumber  // autocomplete!
 *   }
 */
export type ConnectionSlotContext<D = Record<string, any>> = Omit<
  ConnectionsSlotContextMap["connection-status"],
  "channelData" | "connection"
> & {
  channelData?: D | null;
  connection?: IWhatsappConnection<D>;
};

/** Alias retrocompatível (nome antigo). */
export type ConnectionStatusContext = ConnectionSlotContext;

/** connection-form-preview — o formulário, antes de salvar. */
export type ConnectionFormPreviewContext<D = Record<string, any>> = Omit<
  ConnectionsSlotContextMap["connection-form-preview"],
  "channelData"
> & { channelData: D };
